import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {useKeyboard} from "../../hooks/keyboard"
import {useRPC} from "../../hooks/rpc"
import {useMessage} from "../../hooks/message"
import {onClickHandler} from "../../utils/onclick"
import {removeSession} from "../../utils/session-storage"
import {clearUserAction} from "../../store/user/action"
import {useLocalization} from "../../hooks/localization"
import {useHistory} from "react-router-dom"
import {RadioButton} from "../AllControlsScreen"
import "./index.scss"

type UserAgreementsProps = {
    accepted?: () => void
    rejected?: () => void
    isReadOnly?: boolean
}

export const UserAgreementScreen = memo(function UserAgreementScreen({
    accepted,
    rejected,
    isReadOnly = false
}: UserAgreementsProps) {
    const {t} = useLocalization()
    const [active, setActive] = useState(0)
    const onClickActive = useCallback((which: number) => setActive(which), [])
    return (
        <div className="agreement-overlay">
            <div className="agreement-container">
                <div className="agreement-title">
                    <span>{t("user_agreements")}</span>
                </div>
                <div className="agreements">
                    <span className={active === 0 ? "active" : ""} {...onClickHandler(() => onClickActive(0))}>
                        KVKK Metni
                    </span>
                    <span className={active === 1 ? "active" : ""} {...onClickHandler(() => onClickActive(1))}>
                        Aydınlatma Metni
                    </span>
                </div>
                <div className="agreement-content">
                    {renderIf(active === 0)(
                        () => (
                            <div className="agreement-field">
                                <h3>
                                    GST ELEKTRONİK A.Ş 6698 SAYILI KİŞİSEL VERİLERİN KORUNMASI KANUNU KAPSAMINDA KİŞİSEL
                                    VERİLERİN KORUNMASI VE İŞLENMESİ POLİTİKASI
                                </h3>
                                <b>1. Giriş</b>
                                <b>1.1. Politikanın Amacı</b>
                                <p>
                                    6698 sayılı Kişisel Verilerin Korunması Kanunu (“Kanun”) kapsamında GST Elektronik
                                    Anonim Şirketi (“İnohom”) olarak, kişisel verilerin yasalara uygun olarak işlenmesi
                                    ve korunması en mühim önceliklerimiz arasındadır. Tüm planlama ve iş
                                    faaliyetlerimizde de aynı önceliği takip etmekteyiz. Bu çerçevede Kanun’un 10.
                                    maddesi gereğince sizleri aydınlatmak; kişisel verilerin işlenmesi ve korunması
                                    kapsamında uygulayacağımız bütün idari ve teknik tedbirleri bildirmek amacıyla işbu
                                    Kişisel Verilerin İşlenmesi ve Korunması Politikası’nı (“Politika”) sizlerin
                                    bilgisine sunarız.
                                </p>
                                <b>1.2. Kapsamı</b>
                                <p>
                                    İşbu Politika, mevcut ve potansiyel müşterilerimizin, çalışanlarımızın, çalışan
                                    adaylarımızın, ziyaretçilerimizin, işbirliği içinde olduğumuz firmaların
                                    çalışanları, hissedarları ve yetkililerinin ve üçüncü kişilerin otomatik olan ya da
                                    herhangi bir veri kayıt sisteminin parçası olmak kaydıyla otomatik olmayan yollarla
                                    işlenen tüm kişisel verilerine ilişkindir. Yukarıda belirtilen kategorilerde yer
                                    alan kişisel veri sahipleri gruplarına ilişkin işbu Politika’nın uygulama kapsamı
                                    Politika’nın tamamı olabileceği gibi (örn. ziyaretçimiz de olan aktif müşterilerimiz
                                    gibi); yalnızca bir kısım hükümleri de (örn. yalnızca ziyaretçilerimiz gibi)
                                    olabilecektir.
                                </p>
                                <b>1.3. Tanımlar</b>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td>Açık Rıza</td>
                                            <td>
                                                Belirli bir konuya ilişkin, bilgilendirilmeye dayanan ve özgür iradeyle
                                                açıklanan rıza.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Anonim Hale Getirme</td>
                                            <td>
                                                Daha öncesinde bir kişiyle ilişkilendirilmiş olan verilerin, başka
                                                verilerle eşleştirilerek dahi hiçbir surette kimliği belirli veya
                                                belirlenebilir gerçek bir kişiyle ilişkilendirilemeyecek hale
                                                getirilmesi.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Çalışan</td>
                                            <td>İnohom bünyesinde görev yürüten gerçek kişiler.</td>
                                        </tr>
                                        <tr>
                                            <td>Çalışan Adayı</td>
                                            <td>
                                                İnohom bünyesinde çalışmayan ancak çalışan adayı statüsünde olan gerçek
                                                kişiler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Kişisel Veri</td>
                                            <td>
                                                Kimliği belirli veya belirlenebilir gerçek kişiye ilişkin her türlü
                                                bilgi.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Veri Sahibi</td>
                                            <td>Kişisel verisi işlenen gerçek kişi.</td>
                                        </tr>
                                        <tr>
                                            <td>Kişisel Verilerin İşlenmesi</td>
                                            <td>
                                                Kişisel verilerin tamamen veya kısmen otomatik olan ya da herhangi bir
                                                veri kayıt sisteminin parçası olmak kaydıyla otomatik olmayan yollarla
                                                elde edilmesi, kaydedilmesi, depolanması, muhafaza edilmesi,
                                                değiştirilmesi, yeniden düzenlenmesi, açıklanması, aktarılması,
                                                devralınması, elde edilebilir hale getirilmesi, sınıflandırılması ya da
                                                kullanılmasının engellenmesi gibi veriler üzerinde gerçekleştirilen her
                                                türlü işlem.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Kanun</td>
                                            <td>
                                                7 Nisan 2016 tarihli ve 29677 sayılı Resmi Gazete’de yayımlanan 6698
                                                sayılı Kişisel Verilerin Korunması Kanunu.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Özel Nitelikli Kişisel Veri</td>
                                            <td>
                                                Irk, etnik köken, siyasi düşünce, felsefi inanç, din, mezhep veya diğer
                                                inançlar, kılık kıyafet, dernek vakıf ya da sendika üyeliği, sağlık,
                                                cinsel hayat, ceza mahkumiyeti ve güvenlik tedbirleriyle ilgili veriler
                                                ile biyometrik ve genetik veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Politika</td>
                                            <td>
                                                GST Elektronik Anomim Şirketi Kişisel Verilerin İşlenmesi ve Korunması
                                                Politikası
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İnohom</td>
                                            <td>GST Elektronik Şirketi</td>
                                        </tr>
                                        <tr>
                                            <td>Veri İşleyen</td>
                                            <td>
                                                Veri sorumlusunun verdiği yetkiye dayanarak onun adına kişisel veri
                                                işleyen gerçek ve tüzel kişidir.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Veri Sorumlusu</td>
                                            <td>
                                                Kişisel verilerin işlenme amaçlarını ve vasıtalarını belirleyen,
                                                verilerin sistematik bir şekilde tutulduğu yeri yöneten kişidir.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Veri Kayıt Sistemi</td>
                                            <td>
                                                Kişisel verilerin belirli kriterlere göre yapılandırılarak işlendiği
                                                kayıt sistemidir.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İş Ortakları</td>
                                            <td>
                                                İnohom’un ticari faaliyetleri çerçevesinde sözleşmesel ilişkiler
                                                kapsamında ortaklık kurduğu kişiler.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <b>1.4. Politikanın Yürürlüğü</b>
                                <p>
                                    İnohom tarafından düzenlenen bu Politika 01.08.2023 tarihinde yürürlüğe girmiş ve
                                    kamuoyuna sunulmuştur. Başta Kanun olmak üzere yürürlükteki mevzuat ile bu
                                    Politika’da yer verilen düzenlemelerin çelişmesi halinde mevzuat hükümleri
                                    uygulanır.
                                </p>
                                <p>
                                    İnohom, yasal düzenlemelere paralel olarak Politika’da değişiklik yapma hakkını
                                    saklı tutar. Politika’nın güncel haline İnohom (www.inohom.com) web sitesinden
                                    erişilebilirsiniz.
                                </p>
                                <b>2. İnohom’nın Yürüttüğü Kişisel Veri İşleme Faaliyetlerine Yönelik Bilgiler</b>
                                <b>2.1. Veri Sahipleri</b>
                                <p>
                                    Politika kapsamındaki veri sahipleri, İnohom tarafından kişisel verileri işlenen,
                                    İnohom çalışanları dışındaki tüm gerçek kişilerdir. Genel olarak veri sahipleri
                                    aşağıdaki şekilde sıralanabilir:
                                </p>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Veri Sahibi Kategorileri</th>
                                            <th>Açıklama</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Hissedar/Ortak</td>
                                            <td>İnohom’da ortaklıkları bulunan gerçek kişileri ifade eder.</td>
                                        </tr>
                                        <tr>
                                            <td>Müşteriler</td>
                                            <td>
                                                İnohom’nın sunduğu ürün ve hizmetlerden yararlanan gerçek kişileri ifade
                                                eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Potansiyel Müşteriler</td>
                                            <td>
                                                İnohom’nın sunduğu ürün ve hizmetlere ilgi gösteren, müşteriye dönüşme
                                                potansiyeli olan gerçek kişileri ifade eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Çalışan</td>
                                            <td>
                                                İnohom bünyesinde görev yaparak kişisel verileri bulunan gerçek kişileri
                                                ifade eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Çalışan Adayları</td>
                                            <td>
                                                İnohom’ya CV göndererek veya başka yöntemlerle iş başvurusu yapan gerçek
                                                kişileri ifade eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Ziyaretçiler</td>
                                            <td>
                                                İnohom’yı herhangi bir nedenden dolayı ziyarete gelen kişileri ifade
                                                eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Meslek Mensubu Gerçek Kişiler</td>
                                            <td>
                                                İnohom bünyesinde gerçekleştirilen faaliyetler kapsamında bilgileri
                                                bulunan gerçek kişileri ifade eder.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Üçüncü Kişiler</td>
                                            <td>
                                                Yukarıda yer verilen veri sahibi kategorileri ile İnohom çalışanları
                                                hariç gerçek kişileri ifade etmektedir.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p>
                                    Yukarıdaki tabloda açıklanan veri sahibi kategorileri genel bilgi paylaşımı amacıyla
                                    belirtilmiştir. Veri sahibinin bu kategorilerden herhangi birinin kapsamına
                                    girmemesi, Kanun’da belirtildiği şekilde veri sahibi niteliğini ortadan
                                    kaldırmamaktadır.
                                </p>
                                <b>2.2. Kişisel Verilerin İşlenme Amaçları</b>
                                <b>
                                    2.2.1. İnohom’un sunduğu ürün ve hizmetlerden ilgili kişileri faydalandırmak için
                                    gerekli çalışmaların ilgili birimler tarafından yapılması ve iş süreçlerinin
                                    yürütülmesi:
                                </b>
                                <ul>
                                    <li>Ürün ve/veya hizmetlerin satış süreçlerinin planlanması ve icra edilmesi,</li>
                                    <li>
                                        Satış sonrası destek hizmetleri aktivitelerinin planlanması ve/veya icra
                                        edilmesi,
                                    </li>
                                    <li>Müşteri ilişkileri yönetimi süreçlerinin planlanması ve icra edilmesi,</li>
                                    <li>Sözleşme süreçlerinin ve/veya hukuki taleplerin takibi,</li>
                                    <li>Müşteri talep ve/veya şikâyetlerinin takibi.</li>
                                </ul>
                                <b>2.2.2. İnohom çalışanlara yönelik süreçlerinin planlanması ve icra edilmesi:</b>
                                <ul>
                                    <li>İş sözleşmesinin kurulması</li>
                                    <li>
                                        Çalışanların işe başvuru ve mülakatı süresince toplanan belgelerinin kayıt
                                        altına alınması
                                    </li>
                                    <li>Personel özlük dosyasının oluşturulması,</li>
                                    <li>
                                        SGK bildirimleri, İŞKUR bildirimleri, karakol bildirimi ile teşvik ve yasal
                                        yükümlülük bilgilendirmesinin yapılması
                                    </li>
                                    <li>Zorunlu bireysel emeklilik sigortası hesabı açılmasının sağlanması,</li>
                                    <li>Çalışanların işe giriş ve çıkışlarının kontrolünün sağlanması</li>
                                    <li>Mesai Takibi yapılması,</li>
                                    <li>Fiziksel Mekân Güvenliğinin Temini</li>
                                    <li>Çalışanlar İçin Yan Haklar ve Menfaatleri Süreçlerinin Yürütülmesi</li>
                                    <li>Denetim & Etik Faaliyetlerinin Yürütülmesi</li>
                                    <li>Erişim Yetkilerinin Yürütülmesi</li>
                                    <li>Faaliyetlerin Mevzuata Uygun Yürütülmesi</li>
                                    <li>Finans ve Muhasebe İşlerinin Yürütülmesi</li>
                                    <li>Firma & Ürün / Hizmetlere Bağlılık Süreçlerinin Yürütülmesi</li>
                                    <li>İnsan Kaynakları Süreçlerinin Planlanması</li>
                                    <li>İş Faaliyetlerinin Yürütülmesi / Denetimi</li>
                                    <li>İnsan Kaynakları Süreçlerinin Planlanması</li>
                                    <li>İş Faaliyetlerinin Yürütülmesi / Denetimi</li>
                                    <li>Talep & Şikâyetlerin Takibi</li>
                                    <li>Tedarik Zinciri Yönetimi Süreçlerinin Yürütülmesi</li>
                                    <li>Veri Sorumlusu Operasyonlarının Güvenliğinin Temini</li>
                                    <li>Yetkili Kişi Kurum ve Kuruluşlara Bilgi Verilmesi</li>
                                    <li>Yönetim Faaliyetlerinin Yürütülmesi</li>
                                    <li>
                                        İcra dosyalarına çalışanların maaş haciz kesintilerine ilişkin ödeme yapılması
                                    </li>
                                    <li>İş kazasının yasal bildirimlerinin yapılması</li>
                                    <li>İş sağlığı ve güvenliği süreçlerinin yürütülmesi</li>
                                    <li>İşe giriş muayenesinin yapılması,</li>
                                    <li>
                                        İşyeri hekiminden sağlık raporu elde edilmesine bağlı süreçlerin yürütülmesi,
                                    </li>
                                    <li>
                                        Rıza vermeniz halinde, tespit edilen sağlık durumunuza göre pozisyon
                                        değişikliklerinin yapılması ve bu yolla sağlığınız için uygun olan iş
                                        pozisyonlarının sizlere sağlanması
                                    </li>
                                    <li>
                                        Şirketimizin sair mevzuattan doğan yükümlülüklerini yerine getirmesini temini
                                        kapsamında, kamu kurumlarına bildirim yapılması amacıyla özelliklerinize göre
                                        gruplandırmaların yapılması
                                    </li>
                                    <li>Mahkeme kararlarının yerine getirilmesi</li>
                                    <li>İşyeri güvenliğinin sağlanması</li>
                                    <li>
                                        Müşteri şikâyetlerinde müşterinin haklı/haksız ayrımının yapılması, müşteri
                                        memnuniyetinin arttırılması, müşteri ihtiyacının anlaşılması ve müşteri ile
                                        ilişkili süreçlerin iyileştirilmesinin sağlanması
                                    </li>
                                    <li>
                                        Müşteriye hizmet kalitesinin değerlendirilmesi ve çalışanlara eğitim verilmesi
                                    </li>
                                    <li>Şirket çalışanlarının performanslarının takibi ve raporlanması</li>
                                    <li>Çalışanlara masraf ödemelerinin yapılması</li>
                                    <li>Çalışanlarla iletişimin sağlanması</li>
                                    <li>
                                        Kendisine araç tahsis edilen veya kullandırılan çalışanın araba kullanmaya ehil
                                        olduğunun, ehliyetini herhangi bir nedenle kaybetmediğinin teyit edilmesi
                                    </li>
                                    <li>Çalışana araç tedarik edilmesi ayarlanmasının sağlanması</li>
                                    <li>Kartvizit basımının sağlanması</li>
                                    <li>
                                        Kargo ve kurye aracılığıyla gelen paketlerin ilgili çalışana iletilmesinin
                                        sağlanması
                                    </li>
                                    <li>
                                        Çalışanların güvenliği ve işin yürütülmesi için şirket aracı kullanımının takip
                                        edilmesi
                                    </li>
                                    <li>Servis ve seyahat organizasyonunun sağlanması</li>
                                    <li>
                                        E-Posta hizmet sağlayıcısına, çalışan verilerinin girilerek çalışanın iş
                                        e-postasının oluşturulması
                                    </li>
                                    <li>Mesai Takibi yapılması</li>
                                    <li>Kutlama amaçlı iletişimin sağlanması</li>
                                    <li>
                                        Eğitim planlamasının yapılması, eğitimlerin raporlaması, eğitim sertifikalarının
                                        hazırlanması, gerçekleşen eğitimlere katılan çalışanların takip edilebilmesi,
                                        çalışanların aldıkları eğitimler sonucu gelişim süreçlerinin takip edilebilmesi
                                    </li>
                                    <li>Kalite kontrolün sağlanması</li>
                                    <li>
                                        Çalışanların izin onayı, bakiye izinlerin görüntülenmesi, izin düzenlemelerinin
                                        yapılması
                                    </li>

                                    <li>Çalışanların işten çıkış işlemlerinin yapılması</li>
                                    <li>Bordro işlemlerinin yapılmasının sağlanması</li>
                                    <li>Çalışanlara maaş ödemelerinin yapılması</li>
                                    <li>
                                        Şirketimiz ve şirketimiz ile iş ilişkisi içerisinde olan kişilerin hukuki ve
                                        ticari güvenliğinin temini; şirketimizin iş süreçlerinin ve iş stratejilerinin
                                        belirlenmesi ve uygulanması,
                                    </li>
                                    <li>
                                        Acil durum listelerinin oluşturulması ve acil durum operasyonlarının
                                        yürütülmesi,
                                    </li>
                                    <li>
                                        Acil durum analiz raporlarının oluşturulması, iş kazası muayenelerinin
                                        gerçekleştirilebilmesi,
                                    </li>
                                    <li>
                                        Şirketimiz ile akdettiğiniz sözleşmede yer alan amaçlar doğrultusunda, etkili
                                        çalışan yönetiminin sürdürülmesi ve geliştirilmesi,
                                    </li>
                                    <li>Görevlendirmelerin yapılması,</li>
                                    <li>
                                        Rıza vermeniz halinde, etkinlik yönetimi, kurum içi eğitim, tanıtım ve kurumsal
                                        iletişim çalışmaları amacıyla fotoğraf ve görüntülerinizin işlenmesine ilişkin
                                        süreçlerin yürütülmesi,
                                    </li>
                                </ul>
                                <b>
                                    2.2.3. İnohom tarafından yürütülen ticari faaliyetlerin gerçekleştirilmesi için
                                    ilgili iş birimleri tarafından gerekli çalışmaların yapılması ve buna bağlı iş
                                    süreçlerinin yürütülmesi:
                                </b>
                                <ul>
                                    <li>Etkinlik yönetimi,</li>
                                    <li>İş faaliyetlerinin planlanması ve icra edilmesi,</li>
                                    <li>Kurumsal iletişim faaliyetlerinin planlanması ve icra edilmesi,</li>
                                    <li>Tedarik zinciri yönetimi süreçlerinin planlanması ve icra edilmesi,</li>
                                    <li>Üretim ve/veya operasyon süreçlerinin planlanması ve icra edilmesi,</li>
                                    <li>Bilgi güvenliği süreçlerinin planlanması, denetimi ve icra edilmesi,</li>
                                    <li>Bilgi teknolojileri altyapısının oluşturulması ve yönetilmesi,</li>
                                    <li>İş ortaklarının bilgiye erişim yetkilerinin planlanması ve icra edilmesi,</li>
                                    <li>Finans ve/veya muhasebe işlerinin takibi,</li>
                                    <li>Kurumsal sürdürülebilirlik faaliyetlerin planlanması ve icra edilmesi,</li>
                                    <li>Kurumsal yönetim faaliyetlerin planlanması ve icra edilmesi,</li>
                                    <li>
                                        İş sürekliliğinin sağlanması faaliyetlerinin planlanması ve/veya icra edilmesi,
                                    </li>
                                    <li>Lojistik faaliyetlerinin planlanması ve icra edilmesi.</li>
                                </ul>
                                <b>
                                    2.2.4. İnohom tarafından sunulan ürün ve hizmetlerin beğeni, kullanım alışkanlıkları
                                    ve ihtiyaçlarına göre özelleştirilerek ilgili kişilere önerilmesi ve tanıtılması
                                    için gerekli olan aktivitelerin planlanması ve icra edilmesi:
                                </b>

                                <ul>
                                    <li>
                                        Pazarlama faaliyetlerine konu edilecek kişilerin, tüketici davranışı kriterleri
                                        doğrultusunda tespiti ve/veya değerlendirilmesi,
                                    </li>
                                    <li>
                                        Kişiye özel pazarlama ve/veya tanıtım aktivitelerinin tasarlanması ve/veya icra
                                        edilmesi,
                                    </li>
                                    <li>
                                        Dijital ve/veya diğer mecralarda reklam ve/veya tanıtım ve/veya pazarlama
                                        aktivitelerinin tasarlanması ve/veya icra edilmesi,
                                    </li>
                                    <li>
                                        Dijital ve/veya diğer mecralarda müşteri kazanım ve/veya mevcut müşterilerde
                                        değer yaratımı üzerine geliştirilecek aktivitelerin tasarlanması ve/veya icra
                                        edilmesi,
                                    </li>
                                    <li>
                                        Pazarlama amacıyla veri analitiği çalışmalarının planlanması ve/veya icra
                                        edilmesi,
                                    </li>
                                    <li>
                                        Ürün ve/veya hizmetlerin pazarlama süreçlerinin planlanması ve icra edilmesi,
                                    </li>
                                    <li>
                                        İnohomin sunduğu ürün ve/veya hizmetlere bağlılık oluşturulması ve/veya
                                        arttırılması süreçlerinin planlanması ve/veya icra edilmesi.
                                    </li>
                                </ul>
                                <b>2.2.5. İnohom’nın ticari ve/veya iş stratejilerinin planlanması ve icra edilmesi:</b>
                                <p>İş ortakları ile olan ilişkilerin yönetilmesi.</p>
                                <b>
                                    2.2.6. İnohom’un ve İnohom ile iş ilişkisi içerisinde olan ilgili kişilerin hukuki,
                                    teknik ve ticari iş güvenliğinin temini:
                                </b>
                                <ul>
                                    <li>Hukuk işlerinin takibi</li>
                                    <li>
                                        İnohom faaliyetlerinin İnohom prosedürleri ve/veya ilgili mevzuata uygun olarak
                                        yürütülmesinin temini için gerekli operasyonel faaliyetlerinin planlanması ve
                                        icra edilmesi,
                                    </li>
                                    <li>Yetkili kuruluşlara mevzuattan kaynaklı bilgi verilmesi,</li>
                                    <li>Ziyaretçi kayıtlarının oluşturulması ve takibi,</li>
                                    <li>Acil durum yönetimi süreçlerinin planlanması ve icra edilmesi,</li>
                                    <li>Şirketler ve ortaklık hukuku işlemlerinin gerçekleştirilmesi,</li>
                                    <li>İnohom denetim faaliyetlerinin planlanması ve icra edilmesi,</li>
                                    <li>
                                        İş sağlığı ve/veya güvenliği süreçlerinin planlanması ve/veya icra edilmesi,
                                    </li>
                                    <li>Kredi süreçleri risk yönetiminin gerçekleştirilmesi,</li>
                                    <li>İnohom yerleşkeleri ve/veya tesislerinin güvenliğinin temini,</li>
                                    <li>İnohom operasyonlarının güvenliğinin temini,</li>
                                    <li>İnohomin finansal risk süreçlerinin planlanması ve/veya icra edilmesi,</li>
                                    <li>İnohom demirbaşlarının ve/veya kaynaklarının güvenliğinin temini.</li>
                                </ul>
                                <b>2.3. Kişisel Veri Kategorileri</b>
                                <p>
                                    İnohom tarafından aşağıdaki şekilde kategorize edilen kişisel veriler, Kanun’da ve
                                    ilgili mevzuatta yer alan kişisel veri işleme şartlarına uygun olarak işlenmektedir:
                                </p>

                                <table>
                                    <thead>
                                        <tr>
                                            <th>Veri Kategorisi</th>
                                            <th>Açıklama</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Kimlik bilgisi</td>
                                            <td>
                                                Ad, soyadı, doğum tarihi, doğum yeri, cinsiyet, medeni durumu, TC kimlik
                                                kartı bilgileri (Din Bilgisi Hariç), vesikalık fotoğraf (biyometrik
                                                fotoğraf değil), nüfus cüzdanı sureti (Din Bilgisi Kısmı mevcut ise
                                                silinmiş olarak), imza, SGK Sicil No
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İletişim bilgisi</td>
                                            <td>Telefon numarası, açık adres bilgisi, e-posta adresi</td>
                                        </tr>
                                        <tr>
                                            <td>Eğitim Verisi</td>
                                            <td>
                                                Öğrenim Durumu, Mezun Olunan Okul ve Bölüm bilgisi, Katılınan Eğitim ve
                                                Kurslar, Bilgisayar Programları Kullanma Yetisi
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İş Deneyimi Verisi</td>
                                            <td>
                                                Özgeçmiş, İş Tecrübeleriniz (İşyeri Unvanı-Telefon Numarası- çalışma
                                                tarihleriniz-göreviniz), Staj Bilgisi, Referans Bilgisi, Kurs-
                                                Sertifikasyon Bilgisi, Meslek İçi Eğitim Bilgisi, Diploma Bilgisi,
                                                Yabancı Dil Bilgisi
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Finansal Veri ve Özlük Verisi</td>
                                            <td>
                                                Finansal ve maaş detayları, Banka Hesap Bilgisi, bordrolar, prim hak
                                                edişleri, prim tutarları, icra takip dosyalarına ilişkin dosya ve borç
                                                bilgileri, asgari geçim indirimi bilgisi, Ücret Hesap Pusulası, SGK
                                                Aylık Prim ve Hizmet Belgesi, SGK İşe giriş-çıkış bildirgeleri, İşkur
                                                Kayıt Belgesi
                                            </td>
                                        </tr>

                                        <tr>
                                            <td>Aile ve Yakını Verisi</td>
                                            <td>
                                                Eş ve Çocuklarının Adı, Soyadı, T.C. Kimlik Numarası Cinsiyeti, Doğum
                                                Tarihi, Yakını Adı, Soyadı ve Telefon Numarası,
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Sağlık Verisi</td>
                                            <td>
                                                Sakatlık durumu/tanımı/yüzdesi, sağlık verisi, kan grubu, sağlık
                                                raporları, işbaşı sağlık raporu, akciğer grafisi, işitme testi, işyeri
                                                hekiminin imzalattığı işe giriş ve periyodik muayene formları, sağlık ve
                                                doğum izni bilgileri
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Lokasyon bilgisi</td>
                                            <td>
                                                Veri sahibinin konumunu tespit etmeye yarayan bilgiler (örn. araç
                                                kullanımı sırasında edinilen konum bilgileri).
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Müşteri bilgisi</td>
                                            <td>
                                                Ürün ve hizmetlerimizden faydalanan müşterilere ait bilgiler (örn.
                                                müşteri no, meslek bilgisi, vb.).
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Müşteri işlem bilgisi</td>
                                            <td>
                                                Ürün ve hizmetlerimizden faydalanan müşteriler tarafından
                                                gerçekleştirilen her türlü işleme ilişkin bilgiler .
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Fiziksel mekân güvenlik bilgisi</td>
                                            <td>
                                                Fiziksel mekâna girişte, fiziksel mekânın içerisinde kalış sırasında
                                                alınan kamera kayıtları, parmak izi kayıtları gibi kayıtlar ve belgelere
                                                ilişkin kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İşlem güvenliği bilgisi</td>
                                            <td>
                                                İnohom’nın ticari faaliyetlerini yürütürken teknik, idari, hukuki ve
                                                ticari güvenliği sağlamak için işlenen kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Finansal bilgi</td>
                                            <td>
                                                İnohom’nın kişisel veri sahibi ile kurmuş olduğu hukuki ilişkinin tipine
                                                göre yaratılan her türlü finansal sonucu gösteren bilgi, belge ve
                                                kayıtlara ilişkin işlenen kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Çalışan adayı bilgisi</td>
                                            <td>
                                                İnohom’nın çalışanı olmak için başvuruda bulunmuş veya ticari teamül ve
                                                dürüstlük kuralları gereği insan kaynakları ihtiyaçları doğrultusunda
                                                çalışan adayı olarak değerlendirilmiş veya İnohom ile çalışma ilişkisi
                                                içerisinde olan bireylerle ilgili işlenen kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Hukuki işlem ve uyum bilgisi</td>
                                            <td>
                                                İnohom’nın hukuki alacak ve haklarının tespiti, takibi ve borçlarının
                                                ifası ile kanuni yükümlülükleri ve İnohom politikalarına uyum kapsamında
                                                işlenen kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Denetim ve teftiş bilgisi</td>
                                            <td>
                                                İnohom’nın kanuni yükümlülükleri ve İnohom politikalarına uyumu
                                                kapsamında işlenen kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Özel nitelikli veri</td>
                                            <td>
                                                Kişilerin ırkı, etnik kökeni, siyasi düşüncesi, felsefi inancı, dini,
                                                mezhebi veya diğer inançları, kılık ve kıyafeti, dernek, vakıf ya da
                                                sendika üyeliği, sağlığı, cinsel hayatı, ceza mahkûmiyeti ve güvenlik
                                                tedbirleriyle ilgili verileri ile biyometrik ve genetik verileri.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Pazarlama bilgisi</td>
                                            <td>
                                                İnohom’un sunduğu ürün ve hizmetlerin kişisel veri sahibinin kullanım
                                                alışkanlıkları, beğenisi ve ihtiyaçları doğrultusunda özelleştirilerek
                                                pazarlamasının yapılmasına yönelik işlenen kişisel veriler ve bu işleme
                                                sonuçları neticesinde yaratılan rapor ve değerlendirmeler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Talep/şikâyet yönetimi bilgisi</td>
                                            <td>
                                                İnohom’a yöneltilmiş olan her türlü talep veya şikâyetin alınması ve
                                                değerlendirilmesine ilişkin kişisel veriler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İtibar yönetimi bilgisi</td>
                                            <td>
                                                İnohom’un ticari itibarını korumak maksatlı toplanan bilgiler ve buna
                                                ilişkin oluşturulan değerlendirme raporları ile alınan aksiyonlarla
                                                ilgili bilgiler.
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Olay yönetimi bilgisi</td>
                                            <td>
                                                İnohom’un ticari hak ve menfaatleri ile müşterilerinin hak ve
                                                menfaatlerini korumak maksatlı gelişen olaylara karşı gerekli hukuki,
                                                teknik ve idari tedbirlerin alınmasına yönelik olarak işlenen kişisel
                                                veriler.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <b>3. Kişisel Verilerin İşlenmesine İlişkin İlke ve Şartlar</b>
                                <p>
                                    İnohom, Kanun’un 4. maddesine uygun olarak kişisel verilerin işlenmesi konusunda;
                                    hukuka ve dürüstlük kurallarına uygun, doğru ve gerektiğinde güncel, belirli, açık
                                    ve meşru amaçlar güderek, amaç ile bağlantılı, sınırlı ve ölçülü bir biçimde kişisel
                                    veri işleme faaliyetinde bulunmaktadır. İnohom, kanunlarda öngörülen veya kişisel
                                    veri işleme amacının gerektirdiği süre kadar kişisel verileri muhafaza eder.
                                </p>
                                <b>3.1. Kişisel Verilerin İşlenmesine İlişkin İlkeler</b>
                                <p>
                                    İnohom, Kanun’nun 10. maddesine uygun olarak veri sahiplerini aydınlatmaktır ve rıza
                                    alınması gereken durumlarda veri sahiplerinden rızalarını talep ederek bu kişisel
                                    verileri aşağıda belirtilen ilkeleri esas alarak işler.
                                </p>
                                <b>3.1.1. Verilerin Hukuka ve Dürüstlük Kuralına Uygun İşlenmesi</b>
                                <p>
                                    İnohom, kişisel verilerin işlenmesinde hukuksal düzenlemelerle getirilen ilkeler ile
                                    genel güven ve dürüstlük kuralına uygun hareket etmektedir. Dürüstlük kuralına uygun
                                    olma ilkesi uyarınca İnohom veri işlemedeki hedeflerine ulaşmaya çalışırken, ilgili
                                    kişilerin çıkarlarını ve makul beklentilerini dikkate almaktadır.
                                </p>
                                <b>3.1.2. Kişisel Verilerin Doğru ve Gerektiğinde Güncel Olmasının Sağlanması</b>
                                <p>
                                    Kişisel verilerin doğru ve güncel bir şekilde tutulması, İnohom açısından ilgili
                                    kişinin temel hak ve özgürlüklerinin korunması için gereklidir. Kişisel verilerin
                                    doğru ve gerektiğinde güncel olmasının sağlanması noktasında İnohom’nın aktif özen
                                    yükümlülüğü bulunmaktadır. Bu sebeple İnohom tarafından veri sahibi olan ilgili
                                    kişinin bilgilerinin doğru ve güncel olarak tutulması için bütün iletişim kanalları
                                    açıktır.
                                </p>
                                <b>3.1.3. Verilerin Belirli, Açık ve Meşru Amaçlarla İşlenmesi</b>
                                <p>
                                    İnohom, meşru ve hukuka uygun olan kişisel veri işleme amacını açık ve kesin olarak
                                    belirlemektedir. Yürütmekte olduğu ticari faaliyet ile bağlantılı ve bunlar için
                                    gerekli olan kadar kişisel veriyi işlemektedir.
                                </p>
                                <b>3.1.4. Verilerin İşlendikleri Amaçla Bağlantılı, Sınırlı ve Ölçülü Olması</b>
                                <p>
                                    İnohom; kişisel verileri, iştigal konusu ile ilgili ve işinin yürütülmesi için
                                    gerekli olan amaçlar dahilinde işlemektedir. Bu sebeple, kişisel verileri belirlenen
                                    amaçların gerçekleştirilebilmesine elverişli bir biçimde işlemekte ve amacın
                                    gerçekleştirilmesiyle ilgili olmayan veya ihtiyaç duyulmayan kişisel verilerin
                                    işlenmesinden kaçınmaktadır.
                                </p>
                                <b>
                                    3.1.5. Verilerin İlgili Mevzuatta Öngörülen veya İşlendikleri Amaç için Gerekli Olan
                                    Süre Kadar Muhafaza Edilmesi
                                </b>
                                <p>
                                    İnohom, kişisel verileri ancak ilgili mevzuatta belirtildiği veya işlendikleri amaç
                                    için gerekli olan süre kadar muhafaza etmektedir. Bu kapsamda; öncelikle ilgili
                                    mevzuatta kişisel verilerin saklanması için bir süre öngörülüp öngörülmediğini
                                    tespit etmekte, bir süre belirlenmişse bu süreye uygun davranmakta, bir süre
                                    belirlenmemişse kişisel verileri işlendikleri amaç için gerekli olan süre kadar
                                    saklamaktadır. Kişisel veri işleme amacının ortadan kalkmasından sonra ya da
                                    mevzuatta öngörülen sürenin dolması ile birlikte kişisel veriler İnohom tarafından
                                    silinmekte, yok edilmekte veya anonimleştirilmektedir.
                                </p>
                                <b>3.2. Kişisel Verilerin İşlenmesine İlişkin Şartlar</b>
                                <p>
                                    Kanun’un 5. Maddesinde yer alan kişisel veri işleme şartlarından en az birinin
                                    varlığı halinde kişisel verileriniz İnohom tarafından işlenmektedir.
                                </p>
                                <b>3.2.1. Kişisel veri sahibinin açık rızasının olması</b>
                                <p>
                                    Kişisel verilerin işlenme şartlarından biri sahibinin açık rızasıdır. Kişisel veri
                                    sahibinin açık rızası belirli bir konuya ilişkin, bilgilendirilmeye dayalı olarak ve
                                    özgür iradeyle açıklanmalıdır.
                                </p>
                                <p>
                                    Kişisel verilerin, kişisel veri sahibinin açık rıza vermesine bağlı olarak işlenmesi
                                    için, müşteri, potansiyel müşteri ve ziyaretçilerden ilgili yöntemler ile açık
                                    rızaları alınmaktadır.
                                </p>
                                <b>3.2.2. Kişisel veri işleme faaliyetlerinin kanunlarda açıkça ön görülmesi</b>
                                <p>
                                    Veri sahibinin kişisel verileri, kanunda açıkça öngörülmesi halinde veri sahibinin
                                    açık rızası olmadan da hukuka uygun olarak işlenebilmektedir.
                                </p>

                                <b>3.2.3. Fiili imkânsızlık sebebiyle kişinin açık rızasının alınamaması</b>
                                <p>
                                    Fiili imkânsızlık nedeniyle rızasını açıklayamayacak durumda olan veya rızasına
                                    geçerlilik tanınmayacak olan kişinin kendisinin veya başka bir kişinin hayatı ya da
                                    beden bütünlüğünü korumak için kişisel verisinin işlenmesinin zorunlu olması halinde
                                    veri sahibinin kişisel verileri işlenebilmektedir.
                                </p>

                                <b>
                                    3.2.4. Kişisel verinin, bir sözleşmenin kurulması veya ifasıyla doğrudan ilgisi
                                    olması
                                </b>
                                <p>
                                    Bir sözleşmenin kurulması veya ifasıyla doğrudan doğruya ilgili olması kaydıyla,
                                    sözleşmenin taraflarına ait kişisel verilerin işlenmesinin gerekli olması halinde
                                    kişisel verilerin işlenmesi mümkündür.
                                </p>

                                <b>3.2.5. İnohom’un hukuki yükümlülüğünü yerine getirmesi</b>
                                <p>
                                    İnohom’un veri sorumlusu olarak hukuki yükümlülüklerini yerine getirmesi için
                                    işlenmenin zorunlu olması durumunda veri sahibinin kişisel verileri
                                    işlenebilmektedir.
                                </p>

                                <b>3.2.6. Veri sahibinin kişisel verilerinin alenileştirilmesi</b>
                                <p>
                                    Veri sahibinin, kişisel verisini kendisi tarafından alenileştirilmiş olması halinde
                                    ilgili kişisel veriler işlenebilecektir.
                                </p>

                                <b>3.2.7. Bir hakkın tesisi veya korunması için veri işlemenin zorunlu olması</b>
                                <p>
                                    Bir hakkın tesisi, kullanılması veya korunması için veri işlemenin zorunlu olması
                                    halinde veri sahibinin kişisel verileri işlenebilecektir.
                                </p>

                                <b>3.2.8 İnohom’un meşru menfaati için veri işlemenin zorunlu olması</b>
                                <p>
                                    Kişisel veri sahibinin temel hak ve özgürlüklerine zarar vermemek kaydıyla,
                                    İnohom’un meşru menfaatleri için veri işlemesinin zorunlu olması halinde veri
                                    sahibinin kişisel verileri işlenebilecektir.
                                </p>

                                <b>3.3. Özel Nitelikli Kişisel Verilerin İşlenmesi</b>
                                <p>
                                    İnohom tarafından, Kanun ile “özel nitelikli” olarak belirlenen kişisel verilerin
                                    işlenmesinde, Kanun’da öngörülen düzenlemelere hassasiyetle uygun davranılmaktadır.
                                </p>
                                <p>
                                    İnohom tarafından; özel nitelikli kişisel veriler, KVK Kurulu tarafından
                                    belirlenecek olan yeterli önlemlerin alınması kaydıyla aşağıdaki durumlarda
                                    işlenmektedir:
                                </p>
                                <ul>
                                    <li>Kişisel veri sahibinin açık rızası var ise veya</li>
                                    <li>Kişisel veri sahibinin açık rızası yok ise;</li>
                                    <li>
                                        Kişisel veri sahibinin sağlığı ve cinsel hayatı dışındaki özel nitelikli kişisel
                                        veriler, kanunlarda öngörülen hallerde,
                                    </li>
                                    <li>
                                        Kişisel veri sahibinin sağlığına ve cinsel hayatına ilişkin özel nitelikli
                                        kişisel verileri ise ancak kamu sağlığının korunması, koruyucu hekimlik, tıbbi
                                        teşhis, tedavi ve bakım hizmetlerinin yürütülmesi, sağlık hizmetleri ile
                                        finansmanının planlanması ve yönetimi amacıyla, sır saklama yükümlülüğü altında
                                        bulunan kişiler veya yetkili kurum ve kuruluşlar tarafından işlenmektedir.
                                    </li>
                                </ul>
                                <b>4. Kişisel Verilerin Aktarılması</b>
                                <p>
                                    İnohom hukuka uygun olan kişisel veri işleme amaçları doğrultusunda gerekli güvenlik
                                    önlemlerini alarak veri sahibinin kişisel verilerini yurt içi veya yurt dışındaki
                                    üçüncü kişilere aktarabilmektedir. İnohom bu doğrultuda Kanun’un 8. maddesinde
                                    öngörülen düzenlemelere uygun hareket etmektedir.
                                </p>

                                <b>4.1 Kişisel verilerin yurt içindeki üçüncü kişilere aktarımı</b>
                                <p>
                                    Kanun’un 5. ve 6. maddesinde yer alan ve bu Politika’nın 3. Başlığı altında
                                    açıklanmış olan veri işleme şartlarından en az birinin varlığı halinde ve veri
                                    işleme şartlarına ilişkin temel ilkelere uymak şartıyla kişisel verileriniz İnohom
                                    tarafından aktarılabilmektedir.
                                </p>

                                <b>4.2. Kişisel verilerin yurt dışındaki üçüncü kişilere aktarımı</b>
                                <p>
                                    İnohom bu Politika’nın 3. Başlığı altında açıklanmış olan veri işleme şartlarından
                                    en az birinin varlığı halinde ve gerekli güvenlik önlemlerini alarak kişisel veri
                                    sahibinin kişisel verilerini yurt dışındaki üçüncü kişilere aktarabilmektedir.
                                    İnohom tarafından kişisel veriler; KVK Kurulu tarafından yeterli korumaya sahip
                                    olduğu ilan edilen yabancı ülkelere (“Yeterli Korumaya Sahip Yabancı Ülke”) veya
                                    yeterli korumanın bulunmaması durumunda Türkiye’deki ve ilgili yabancı ülkedeki veri
                                    sorumlularının yeterli bir korumayı yazılı olarak taahhüt ettiği ve KVK Kurulu’nun
                                    izninin bulunduğu yabancı ülkelere (“Yeterli Korumayı Taahhüt Eden Veri Sorumlusunun
                                    Bulunduğu Yabancı Ülke”) aktarılmaktadır. İnohom bu doğrultuda Kanun’un 9.
                                    maddesinde öngörülen düzenlemelere uygun hareket etmektedir.
                                </p>

                                <b>4.3 Kişisel verilerin aktarıldığı üçüncü kişiler ve aktarılma amaçları</b>
                                <p>
                                    Kanunun genel ilkeleri ile 8. ve 9. maddelerinde yer alan veri işleme şartları
                                    dâhilinde İnohom, aşağıdaki tabloda kategorizasyonu yapılmış taraflara veri aktarımı
                                    gerçekleştirebilmektedir:
                                </p>

                                <table>
                                    <thead>
                                        <tr>
                                            <th>Veri Aktarımı Yapılabilecek Kişiler</th>
                                            <th>Tanım</th>
                                            <th>Amaç</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>İş Ortağı</td>
                                            <td>
                                                İnohom’un ticari faaliyetlerini yürütürken iş ortaklığı kurduğu taraflar
                                            </td>
                                            <td>
                                                İş ortaklığının kurulma amaçlarının yerine getirilmesini temin etmek
                                                amacıyla sınırlı olarak kişisel verilerin paylaşımı
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Hissedarlar</td>
                                            <td>
                                                İlgili mevzuat hükümlerine göre İnohom’un ticari faaliyetlerine ilişkin
                                                stratejilerinin ve denetim faaliyetlerinin tasarlanması konusunda
                                                yetkili olan hissedarlar
                                            </td>
                                            <td>
                                                İnohom’un ticari faaliyetlerine ilişkin stratejilerin tasarlanması ve
                                                denetim amaçlarıyla sınırlı olarak kişisel verilerin paylaşımı
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İnohom Yetkilileri</td>
                                            <td>Yönetim kurulu üyeleri ve diğer yetkilendirilen kişiler</td>
                                            <td>
                                                İnohom’un ticari faaliyetlerine ilişkin stratejilerin tasarlanması, en
                                                üst düzeyde yönetiminin sağlanması ve denetim amaçlarıyla sınırlı olarak
                                                kişisel verilerin paylaşımı
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Hukuken Yetkili Kamu Kurum ve Kuruluşları</td>
                                            <td>
                                                Hukuken İnohom’dan bilgi ve belge almaya yetkili kamu kurum ve
                                                kuruluşları
                                            </td>
                                            <td>
                                                İlgili kamu kurum ve kuruluşlarının bilgi talep etme amacıyla sınırlı
                                                olarak kişisel veri paylaşımı
                                            </td>
                                        </tr>

                                        <tr>
                                            <td>Hukuken Yetkili Özel Hukuk Kişileri</td>
                                            <td>
                                                Hukuken İnohom’dan bilgi ve belge almaya yetkili özel hukuk kişileri
                                            </td>
                                            <td>
                                                İlgili özel hukuk kişilerinin hukuki yetkisi dâhilinde talep ettiği
                                                amaçla sınırlı olarak verilerin paylaşımı
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <b>Kişisel Verilerin Güvenliğinin Sağlanması</b>
                                <p>
                                    İnohom; kişisel verilerinizin güvenli bir şekilde saklanması, hukuka aykırı olarak
                                    işlenmesi ve erişilmesinin önlenmesi ile kişisel verilerinizin hukuka uygun olarak
                                    imha edilmesi için gerekli tüm teknik ve idari tedbirleri almaktadır.
                                </p>
                                <b>6.1. Teknik Tedbirler</b>
                                <p>
                                    İnohom, kişisel verilerinizin saklandığı ortamlarda, ilgili verinin ve tutulduğu
                                    ortamın niteliklerine uygun düştüğü ölçüde aşağıdaki teknik tedbirleri almaktadır:
                                </p>
                                <ul>
                                    <li>Sistem güvenliği geliştirilmektedir.</li>
                                    <li>
                                        Kullanıcıların bilişim sistemlerine erişimi sınırlandırılmakta, erişim ve yetki
                                        matrisi oluşturulmaktadır.
                                    </li>
                                    <li>
                                        Kurumun bilişim sistemleri teçhizatı, yazılım ve verilerin fiziksel güvenliği
                                        için önlemler alınmaktadır.
                                    </li>
                                    <li>
                                        Çevresel tehditlere karşı bilişim sistemleri güvenliğinin sağlanması için,
                                        donanımsal (sistem odasına sadece yetkili personelin girişini sağlayan erişim
                                        kontrol sistemi, 7/24 çalışan izleme sistemi, yangın söndürme sistemi) ve
                                        yazılımsal (güvenlik duvarları, atak önleme sistemleri, ağ erişim kontrolü,
                                        zararlı yazılımları engelleyen sistemler vb.) önlemler alınmaktadır.
                                    </li>
                                    <li>
                                        Kanun’un 12. maddesi uyarınca, kişisel verilerin saklandığı her türlü dijital
                                        ortamı, bilgi güvenliği gereksinimlerini sağlayacak şekilde şifreli veya
                                        kriptografik yöntemler ile korur.
                                    </li>
                                    <li>
                                        Kişisel verilerin hukuka aykırı işlenmesini önlemeye yönelik riskler
                                        belirlenmekte, bu risklere uygun teknik tedbirlerin alınması sağlanmakta ve
                                        alınan tedbirlere yönelik teknik kontroller yapılmaktadır.
                                    </li>
                                    <li>
                                        İnohom, silinen kişisel verilerin ilgili kullanıcılar için erişilemez ve tekrar
                                        kullanılamaz olması için gerekli tedbirleri almaktadır.
                                    </li>
                                    <li>
                                        Güvenlik açıkları takip edilerek uygun güvenlik yamaları yüklenmekte ve bilgi
                                        sistemleri güncel halde tutulmaktadır.
                                    </li>
                                    <li>
                                        Kişisel verilerin işlendiği elektronik ortamlarda parolalar kullanılmaktadır.
                                    </li>
                                    <li>
                                        Kişisel verilerin işlendiği elektronik ortamlarda güvenli kayıt tutma (loglama)
                                        sistemleri (kısmen) kullanılmaktadır.
                                    </li>

                                    <li>
                                        Kişisel verilerin güvenli olarak saklanmasını sağlayan veri yedekleme
                                        programları kullanılmaktadır.
                                    </li>
                                    <li>
                                        Elektronik olan veya olmayan ortamlarda saklanan kişisel verilere erişim, erişim
                                        prensiplerine göre sınırlandırılmaktadır.
                                    </li>
                                    <li>
                                        Özel nitelikli kişisel verilerin işlendiği, muhafaza edildiği ve/veya erişildiği
                                        fiziksel ortamlarda güvenlik önlemleri alınmaktadır
                                    </li>
                                    <li>
                                        Kişisel verilerin yok edilmesi; geri dönüştürülemeyecek ve denetim izi
                                        bırakmayacak şekilde sağlanmaktadır.
                                    </li>
                                    <li>Ağ güvenliği ve uygulama güvenliği sağlanmaktadır.</li>
                                    <li>Anahtar yönetimi uygulanmaktadır.</li>
                                    <li>
                                        Bilgi teknolojileri sistemleri tedarik, gelistirme ve bakımı kapsamındaki
                                        güvenlik önlemleri alınmaktadır.
                                    </li>
                                    <li>Bulutta depolanan kişisel verilerin güvenliği sağlanmaktadır.</li>
                                    <li>
                                        Çalışanlar için veri güvenliği hükümleri içeren disiplin düzenlemeleri
                                        mevcuttur.
                                    </li>
                                    <li>Erişim logları düzenli olarak tutulmaktadır.</li>
                                    <li>Gizlilik taahhütnameleri yapılmaktadır.</li>
                                    <li>
                                        Görev değişikliği olan ya da işten ayrılan çalışanların bu alandaki yetkileri
                                        kaldırılmaktadır.
                                    </li>
                                    <li>Güncel anti-virüs sistemleri kullanılmaktadır.</li>
                                    <li>Güvenlik duvarları kullanılmaktadır.</li>
                                    <li>İmzalanan sözleşmeler veri güvenliği hükümleri içermektedir.</li>
                                    <li>Kişisel veri güvenliği politika ve prosedürleri belirlenmiştir.</li>
                                    <li>Kişisel veri güvenliği sorunları hızlı bir şekilde raporlanmaktadır.</li>
                                    <li>Kişisel veri güvenliğinin takibi yapılmaktadır.</li>
                                    <li>
                                        Kişisel veri içeren fiziksel ortamlara giriş çıkışlarla ilgili gerekli güvenlik
                                        önlemleri alınmaktadır.
                                    </li>
                                    <li>Kişisel veri içeren ortamların güvenliği sağlanmaktadır.</li>

                                    <li>Kişisel veriler mümkün olduğunca azaltılmaktadır.</li>
                                    <li>
                                        Kullanıcı hesap yönetimi ve yetki kontrol sistemi uygulanmakta olup bunların
                                        takibi de yapılmaktadır.
                                    </li>
                                    <li>
                                        Kurum içi periyodik ve/veya rastgele denetimler yapılmakta ve yaptırılmaktadır.
                                    </li>
                                    <li>Log kayıtları kullanıcı müdahalesi olmayacak şekilde tutulmaktadır.</li>
                                    <li>Mevcut risk ve tehditler belirlenmiştir.</li>
                                    <li>Saldırı tespit ve önleme sistemleri kullanılmaktadır.</li>
                                    <li>Şifreleme yapılmaktadır.</li>
                                    <li>Sızma testi uygulanmaktadır.</li>
                                    <li>
                                        Veri işleyen hizmet sağlayıcılarının veri güvenliği konusunda belli aralıklarla
                                        denetimi sağlanmaktadır.
                                    </li>
                                    <li>
                                        Veri işleyen hizmet sağlayıcılarının, veri güvenliği konusunda farkındalığı
                                        sağlanmaktadır.
                                    </li>
                                    <li>Veri kaybı önleme yazılımları kullanılmaktadır.</li>
                                </ul>
                                <b>6.2. İdari Tedbirler</b>
                                <p>
                                    İnohom tarafından, işlenen kişisel verilerinizle ilgili olarak alınan idari
                                    tedbirler aşağıda sayılmıştır:
                                </p>
                                <ul>
                                    <li>Erişim yetki kısıtlamalarının öngörülmüştür.</li>
                                    <li>Veri minimizasyonunun sağlanmıştır.</li>
                                    <li>Veri saklama sürelerinin tespit edilmiştir.</li>
                                    <li>
                                        İnohom çalışanlarına, gerçekleştirilen/gerçekleştirilecek toplantı ve
                                        eğitimlerle farkındalık yaratılmaktadır.
                                    </li>
                                    <li>İnohom 'in iş ve operasyonel süreçleri Kanun’a uyumlu hale getirilmektedir.</li>
                                    <li>
                                        Veri envanteri ile veri işleme şartlarının hangi durumlarda gerçekleştiği tespit
                                        edilmiştir.
                                    </li>
                                </ul>

                                <b>6.3. Kişisel Verilerin Korunması Konusunda Alınan Tedbirlerin Denetimi</b>
                                <p>
                                    Kurumumuz, KVK Kanunu’nun 12. maddesine uygun olarak, kendi bünyesinde gerekli
                                    denetimleri yapmakta veya yaptırmaktadır. Bu denetim sonuçları, Kurumun iç işleyişi
                                    kapsamında yeniden değerlendirilmekte ve alınan tedbirlerin iyileştirilmesi için
                                    gerekli faaliyetler yürütülmektedir.
                                </p>
                                <b>6.4. Kişisel Verilerin Yetkisiz Bir Şekilde İfşası Durumunda Alınacak Tedbirler</b>
                                <p>
                                    Kurumumuz, KVK Kanunu’nun 12. maddesine uygun olarak işlenen kişisel verilerin
                                    kanuni olmayan yollarla başkaları tarafından elde edilmesi halinde bu durumu en kısa
                                    sürede ilgili kişisel veri sahibine ve KVK Kurulu’na bildirilmesini sağlayan sistemi
                                    yürütmektedir.
                                </p>
                                <b>Kişisel Verilerin Saklanma Süreleri</b>
                                <p>
                                    Kurumumuz, ilgili kanunlarda ve mevzuatta öngörülmesi durumunda kişisel verileri
                                    ilgili mevzuatta belirtilen süre boyunca saklanmaktadır.
                                </p>

                                <p>
                                    Kişisel verilerin ne kadar süre boyunca saklanması gerektiğine ilişkin mevzuatta bir
                                    süre düzenlenmemişse, kişisel veriler kurumun o veriyi işlerken sunduğu hizmetlerle
                                    bağlı olarak Kurumun uygulamaları ve ticari yaşamının teamülleri uyarınca
                                    işlenmesini gerektiren süre kadar işlenmekte, daha sonra silinmekte, yok edilmekte
                                    veya anonim hale getirilmektedir. Bu konu ile ilgili ayrıntılı bilgiye bu
                                    Politika’nın 9. bölümünde yer verilmiştir.
                                </p>
                                <p>
                                    Kişisel verilerin işlenme amacı sona ermiş; ilgili mevzuat ve kurumun belirlediği
                                    saklama sürelerinin de sonuna gelinmişse; kişisel veriler yalnızca olası hukuki
                                    uyuşmazlıklarda delil teşkil etmesi veya kişisel veriye bağlı ilgili hakkın ileri
                                    sürülebilmesi veya savunmanın tesis edilmesi amacıyla saklanabilmektedir. Buradaki
                                    sürelerin tesisinde bahsi geçen hakkın ileri sürülebilmesine yönelik zaman aşımı
                                    süreleri ile zaman aşımı sürelerinin geçmesine rağmen daha önce aynı konularda
                                    Kuruma yöneltilen taleplerdeki örnekler esas alınarak saklama süreleri
                                    belirlenmektedir. Bu durumda saklanan kişisel verilere herhangi bir başka amaçla
                                    erişilmemekte ve ancak ilgili hukuki uyuşmazlıkta kullanılması gerektiği zaman
                                    ilgili kişisel verilere erişim sağlanmaktadır. Burada da bahsi geçen süre sona
                                    erdikten sonra kişisel veriler silinmekte, yok edilmekte veya anonim hale
                                    getirilmektedir.
                                </p>

                                <table>
                                    <thead>
                                        <tr>
                                            <th>Veri Grubu</th>
                                            <th>Saklama Süresi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Çalışan Adaylarına ait kişisel veriler</td>
                                            <td>6 Ay</td>
                                        </tr>
                                        <tr>
                                            <td>Çalışanlara Ait Veriler</td>
                                            <td>10 Yıl</td>
                                        </tr>
                                        <tr>
                                            <td>Muhasebe Kayıtları</td>
                                            <td>
                                                5 – 10 Yıl (Türk Ticaret Kanunu Uyarıca bazı kişisel veri içeren
                                                belgeler 10 yıl, Vergi Usul Kanunu uyarınca kişisel veri içeren belgeler
                                                5 yıl saklanmaktadır)
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>İnohom binalarındaki Güvenlik Kamera Kayıtları</td>
                                            <td>25 gün</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <b>Veri Sahibinin Hakları ve İlgili Hakların Kullanılması</b>
                                <b>8.1 Kişisel veri sahibinin hakları:</b>

                                <ul>
                                    <li>Kişisel verilerinin işlenip işlenmediğini öğrenme,</li>
                                    <li>Kişisel verileri işlenmişse buna ilişkin bilgi talep etme,</li>
                                    <li>
                                        Kişisel verilerin işlenme amacını ve bunların amacına uygun kullanılıp
                                        kullanılmadığını öğrenme,
                                    </li>
                                    <li>
                                        Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri
                                        bilme,
                                    </li>
                                    <li>
                                        Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde bunların
                                        düzeltilmesini isteme ve bu kapsamda yapılan işlemin kişisel verilerin
                                        aktarıldığı üçüncü kişilere bildirilmesini isteme,
                                    </li>
                                    <li>
                                        Kanun ve ilgili diğer kanun hükümlerine uygun olarak işlenmiş olmasına rağmen,
                                        işlenmesini gerektiren sebeplerin ortadan kalkması hâlinde kişisel verilerin
                                        silinmesini veya yok edilmesini isteme ve bu kapsamda yapılan işlemin kişisel
                                        verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,
                                    </li>
                                    <li>
                                        İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi
                                        suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkması halinde bu sonuca
                                        itiraz etme,
                                    </li>
                                    <li>
                                        Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması
                                        hâlinde zararın giderilmesini talep etme.
                                    </li>
                                </ul>
                                <p>
                                    Kişisel verilerin doğrudan veri sahibinden elde edilmemesi halinde; İnohom
                                    tarafından (1) kişisel verilerin elde edilmesinden itibaren makul bir süre
                                    içerisinde, (2) kişisel verilerin veri sahipleri kişi ile iletişim amacıyla
                                    kullanılacak olması durumunda, ilk iletişim kurulması esnasında, (3) kişisel
                                    verilerin aktarılacak olması halinde, en geç kişisel verilerin ilk kez aktarımının
                                    yapılacağı esnada veri sahiplerinin aydınlatılmasına ilişkin faaliyetler
                                    yürütülmektedir.
                                </p>
                                <b>8.2 Kişisel veri sahibinin haklarını ileri süremeyeceği haller:</b>
                                <p>
                                    Kişisel veri sahipleri, Kanun’un 28. maddesi gereğince aşağıdaki haller Kanun
                                    kapsamı dışında tutulduğundan, bu konularda 8.1’de sayılan haklarını ileri
                                    süremezler:
                                </p>

                                <ul>
                                    <li>
                                        Kişisel verilerin, üçüncü kişilere verilmemek ve veri güvenliğine ilişkin
                                        yükümlülüklere uyulmak kaydıyla gerçek kişiler tarafından tamamen kendisiyle
                                        veya aynı konutta yaşayan aile fertleriyle ilgili faaliyetler kapsamında
                                        islenmesi,
                                    </li>
                                    <li>
                                        Kişisel verilerin resmi istatistik ile anonim hâle getirilmek suretiyle
                                        araştırma, planlama ve istatistik gibi amaçlarla işlenmesi,
                                    </li>
                                    <li>
                                        Kişisel verilerin millî savunmayı, millî güvenliği, kamu güvenliğini, kamu
                                        düzenini, ekonomik güvenliği, özel hayatın gizliliğini veya kişilik haklarını
                                        ihlal etmemek ya da suç teşkil etmemek kaydıyla, sanat, tarih, edebiyat veya
                                        bilimsel amaçlarla ya da ifade özgürlüğü kapsamında işlenmesi,
                                    </li>
                                    <li>
                                        Kişisel verilerin millî savunmayı, millî güvenliği, kamu güvenliğini, kamu
                                        düzenini veya ekonomik güvenliği sağlamaya yönelik olarak kanunla görev ve yetki
                                        verilmiş kamu kurum ve kuruluşları tarafından yürütülen önleyici, koruyucu ve
                                        istihbari faaliyetler kapsamında işlenmesi,
                                    </li>
                                    <li>
                                        Kişisel verilerin soruşturma, kovuşturma, yargılama veya infaz işlemlerine
                                        ilişkin olarak yargı makamları veya infaz mercileri tarafından işlenmesi.
                                    </li>
                                </ul>
                                <p>
                                    Kanun’un 28.2. maddesi gereğince; aşağıda sıralanan hallerde kişisel veri sahipleri
                                    zararın giderilmesini talep etme hakkı hariç, 8.1’de sayılan diğer haklarını ileri
                                    süremezler:
                                </p>
                                <ul>
                                    <li>
                                        Kişisel veri işlemenin suç işlenmesinin önlenmesi veya suç soruşturması için
                                        gerekli olması,
                                    </li>
                                    <li>
                                        Kişisel veri sahibinin kendisi tarafından alenileştirilmiş kişisel verilerin
                                        işlenmesi,
                                    </li>
                                    <li>
                                        Kişisel veri işlemenin kanunun verdiği yetkiye dayanılarak görevli ve yetkili
                                        kamu kurum ve kuruluşları ile kamu kurumu niteliğindeki meslek kuruluşlarınca,
                                        denetleme veya düzenleme görevlerinin yürütülmesi ile disiplin soruşturma veya
                                        kovuşturması için gerekli olması,
                                    </li>
                                    <li>
                                        Kişisel veri işlemenin bütçe, vergi ve mali konulara ilişkin olarak Devletin
                                        ekonomik ve mali çıkarlarının korunması için gerekli olması.
                                    </li>
                                </ul>
                                <b>9. Kişisel Verilerin Silinmesi, Yok Edilmesi, Anonim Hale Getirilmesi</b>
                                <p>
                                    Türk Ceza Kanunu’nun 138. maddesinde ve Kanun’un 7. maddesinde düzenlendiği üzere
                                    ilgili kanun hükümlerine uygun olarak işlenmiş olmasına rağmen, işlenmesini
                                    gerektiren sebeplerin ortadan kalkması hâlinde İnohom’un kararına istinaden veya
                                    kişisel veri sahibinin talebi üzerine kişisel veriler silinir, yok edilir veya
                                    anonim hâle getirilir. Bu kapsamda İnohom ilgili yükümlülüğünü yerine getirmek üzere
                                    İnohom içerisinde gerekli teknik ve idari tedbirleri alarak; bu konuda gerekli
                                    işleyiş mekanizmaları geliştirmiş olup; bu yükümlülüklerine uygun davranmak üzere
                                    ilgili iş birimlerini eğitmekte, görevlendirme ve farkındalıklarını sağlamaktadır.
                                </p>
                                <b>Bizimle İletişime Geçin</b>
                                <p>
                                    Kişisel Verilerin Korunması Politikası ile ilgili tüm soru ve görüşlerinizi iletmek
                                    için bize ulaşın!
                                </p>
                                <p>https://inohom.com/akilli-ev-iletisim</p>
                            </div>
                        ),
                        () => (
                            <div className="agreement-field">
                                <h3>
                                    GST ELEKTRONİK A.Ş 6698 SAYILI KİŞİSEL VERİLERİN KORUNMASI KANUNU KAPSAMINDA KİŞİSEL
                                    VERİLERİN KORUNMASI VE İŞLENMESİNE İLİŞKİN AYDINLATMA METNİ
                                </h3>
                                <p>
                                    GST Elektronik Anonim Şirketi (“İnohom”) olarak kişisel verilerinizin 6698 sayılı
                                    Kişisel Verilerin Korunması Kanunu'na (“Kanun”) uygun olarak işlenerek, muhafaza
                                    edilmesine büyük önem veriyoruz. Kanun’un 10. maddesinde yer alan “Veri
                                    Sorumlusu’nun Aydınlatma Yükümlülüğü” başlıklı maddesi uyarınca: veri sorumlusunun
                                    kimliği, kişisel verilerinizin toplanma yöntemi ve hukuki sebebi, bu verilerin hangi
                                    amaçla işleneceği, kimlere ve hangi amaçla aktarılabileceği, veri işleme süresi ve
                                    Kanun’un 11. maddesinde sayılan haklarınızın neler olduğu ile ilgili sizi en şeffaf
                                    şekilde bilgilendirme amacıyla hazırlanmıştır.
                                </p>
                                <b>1. VERİ SORUMLUSUNUN KİMLİĞİ</b>
                                <p>
                                    İnohom tarafından elde edilen kişisel veriler bakımından 6698 sayılı Kişisel
                                    Verilerin Korunması Kanunu ve ilgili düzenlemeler uyarınca “Veri Sorumlusu” sıfatına
                                    haiz olup tarafımıza aşağıda yer verilen iletişim bilgileri aracılığıyla ulaşmanız
                                    mümkündür.
                                </p>
                                <p>
                                    <b>Adres</b> <span>:Kemalöz Mah. 8. Bilgi Sk. No:4/A Merkez/Uşak</span>
                                    <br />
                                    <b>Telefon</b> <span>:+90 850 811 82 83</span>
                                    <br />
                                    <b>E-posta</b> <span>:info@inohom.com</span>
                                </p>
                                <b>2. KİŞİSEL VERİLERİNİZİN TOPLANMA YÖNTEMİ VE HUKUKİ SEBEBİ</b>
                                <p>
                                    Kişisel verileriniz İnohom ile aranızdaki ilişkiye bağlı olarak değişkenlik
                                    gösterebilmekle birlikte, otomatik ya da otomatik olmayan yöntemlerle, İnohom’un
                                    bağlı birimleri, firma içerisinde faaliyetlerin yürütülebilmesi için kullanılan
                                    yazılımlar, kamera çekimi vasıtasıyla ve benzeri vasıtalar ile sözlü, yazılı, basılı
                                    formlarla ya da elektronik olarak ve İnohom’un düzenlediği veya katıldığı
                                    etkinliklerde, İnohom’un meşru menfaati gereği sizden ve/veya şirketinizi temsilen
                                    diğer temsilcilerden/çalışanlardan talep ettiğimiz kişisel verilerinizi, sizin
                                    tarafınızdan ve/veya şirketinizin diğer temsilcilerinin/çalışanları tarafından
                                    Şirketimize fiziksel veya elektronik ortamda iletilmesi ile toplanabilecektir.
                                    Kişisel verileriniz, İnohom ile ilişkiniz devam ettiği müddetçe oluşturularak ve
                                    güncellenerek işlenebilecek ve hem dijital hem de fiziki ortamda muhafaza altında
                                    tutulabilecektir.
                                </p>
                                <p>
                                    Hangi kişisel verilerinizin işlendiğini “İşlenen Kişisel Verileriniz” başlığı
                                    altında, kişisel verileriniz, “Kişisel Verilerinizin İşlenme Amaçları” başlığında
                                    yer alan amaçlar doğrultusunda ve Kanun’un 5. maddesinde belirtilen kişisel veri
                                    işleme şartları ve amaçları kapsamında işlenebilmekte ve aktarılabilmektedir.
                                </p>
                                <p>
                                    Kişisel verileriniz Kanun’un 5. ve 6. maddelerinde belirtilen;
                                    <em style={{textDecoration: "underline"}}>
                                        ''Kanunlarda açıkça öngörülmesi'' , ''Veri sorumlusunun hukuki yükümlülüğünü
                                        yerine getirebilmesi için zorunlu olması'' , ''Bir hakkın tesisi, kullanılması
                                        veya korunması için veri işlemenin zorunlu olması'' , ''Bir sözleşmenin
                                        kurulması veya ifasıyla doğrudan doğruya ilgili olması kaydıyla, sözleşmenin
                                        taraflarına ait kişisel verilerin işlenmesinin gerekli olması'' , '' ilgili
                                        kişinin temel hak ve özgürlüklerine zarar vermemek kaydıyla, veri sorumlusunun
                                        meşru menfaatleri için veri işlenmesinin zorunlu olması'' veya açık rıza hukuki
                                        sebeplerine dayalı olarak toplanmakta ve işlenmektedir.
                                    </em>
                                </p>
                                <b>3. İŞLENEN KİŞİSEL VERİLERİNİZ</b>
                                <p>
                                    Kişisel Verileriniz yasal yükümlülükler gereği veya firma iş ve işlemlerinde daha
                                    uygun bir hizmet verebilmek amacıyla güvenli bir şekilde Kişisel Verilerin Korunması
                                    Kanunu’na uygun bir şekilde işlenmektedir.
                                </p>

                                <table>
                                    <tbody>
                                        <tr>
                                            <td>Kimlik Verisi</td>
                                            <td>
                                                Ad, soyadı, doğum tarihi, doğum yeri, nüfus cüzdanı sureti (Din Bilgisi
                                                Kısmı mevcut ise silinmiş olarak), imza
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Mesleki Deneyim Verisi</td>
                                            <td>Unvan</td>
                                        </tr>
                                        <tr>
                                            <td>İletişim Verisi</td>
                                            <td>Telefon numarası, açık adres bilgisi, e-posta adresi</td>
                                        </tr>
                                        <tr>
                                            <td>Talep/Şikâyet Yönetimi Bilgisi</td>
                                            <td>
                                                İnohom’a yöneltilmiş olan her türlü talep veya şikâyetin alınması ve
                                                değerlendirilmesine ilişkin kişisel veriler
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Finansal Veri ve Özlük Verisi</td>
                                            <td>
                                                Kişinin finansal bilgilerinin bulunduğu veri grubudur (Banka hesap no,
                                                iban no, kredi kartı no, çek bilgisi)
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Dijital Ortam Kullanım Verileri, Çerez Verileri</td>
                                            <td>
                                                Kullanıcıların dijital ortamdaki faaliyetlerinin takibi neticesinde elde
                                                edilen, gezinme süre ve detaylarını içeren veriler, konum verileri,
                                                kullanıcı adı ve şifre gibi her türlü kişisel veriler Kullanıcılara
                                                gösterilen pop-up uyarılar, internet sitesi kullanımına yönelik benzeri
                                                veriler
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Görsel ve İşitsel Veri</td>
                                            <td>Gerçek kişiye ait fotoğraf, kamera kayıtları</td>
                                        </tr>
                                        <tr>
                                            <td>Fiziksel Mekân Güvenlik Bilgisi</td>
                                            <td>
                                                Fiziksel mekâna girişte, fiziksel mekânın içerisinde kalış sırasında
                                                alınan kamera kayıtları ve belgelere ilişkin kişisel veriler
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Hukuki İşlem</td>
                                            <td>
                                                Bu veri kategorisi adli makamlarla tüketici hakem heyetleri ile ilgili
                                                yazışmalarda yer alan bilgiler, dava dosyasındaki bilgiler gibi veri
                                                türlerini ifade etmektedir.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <b>4. KİŞİSEL VERİLERİNİZİN İŞLENME AMAÇLARI</b>
                                <p>
                                    Kişisel verileriniz, İnohom tarafından; yapılan sözleşmesinin ifası ve ilgili
                                    mevzuatlar uyarınca yükümlülüklerin yerine getirilme amacı doğrultusunda Kanun’un 5.
                                    ve 6. maddelerinde belirtilen kişisel veri işleme şartları ve amaçları dâhilinde
                                    aşağıdaki amaçlarla sınırlı olmak üzere işlenebilecektir:
                                </p>

                                <ul>
                                    <li>Denetim Faaliyetlerinin Yürütülmesi,</li>
                                    <li>Faaliyetlerin Mevzuata Uygun Yürütülmesi,</li>
                                    <li>Finans ve Muhasebe İşlerinin Yürütülmesi,</li>
                                    <li>Hukuk İşlerinin Takibi ve Yürütülmesi</li>
                                    <li>İletişim Faaliyetlerinin Yürütülmesi,</li>
                                    <li>İş Faaliyetlerinin Yürütülmesi / Denetimi</li>
                                    <li>İş Sağlığı & Güvenliği Faaliyetlerinin Yürütülmesi</li>
                                    <li>
                                        İş Süreçlerinin İyileştirilmesine Yönelik Önerilerin Alınması ve
                                        Değerlendirilmesi,
                                    </li>
                                    <li>İş Sürekliliğinin Sağlanması Faaliyetlerinin Yürütülmesi</li>
                                    <li>Lojistik Faaliyetlerinin Yürütülmesi</li>
                                    <li>
                                        Mal / Hizmet Satış, Satın Alma, Üretim ve Operasyon Süreçlerinin Yürütülmesi,
                                    </li>
                                    <li>Sözleşme Kurulması ve İfasına yönelik Süreçlerinin Yürütülmesi,</li>
                                    <li>Stratejik Planlama Faaliyetlerinin Yürütülmesi,</li>
                                    <li>Talep / Şikâyetlerin Takibi</li>
                                    <li>Tedarik Zinciri Yönetimi Süreçlerinin Yürütülmesi,</li>
                                    <li>Yetkili Kişi, Kurum ve Kuruluşlara Bilgi Verilmesi</li>
                                    <li>Yönetim Faaliyetlerinin Yürütülmesi,</li>
                                    <li>Müşterilere yönelik kampanyaların oluşturulması,</li>
                                    <li>Hedef kitle belirlenmesi,</li>
                                    <li>
                                        Müşteri hareketlerinin takip edilerek kullanıcı deneyimini arttırıcı
                                        faaliyetlerin yürütülmesi,
                                    </li>
                                    <li>İnternet sitemizin ve mobil uygulamaların geliştirilmesi,</li>
                                    <li>Analiz ve şirket içi raporlama faaliyetlerinin yürütülmesi,</li>
                                </ul>
                                <b>5. İŞLENEN KİŞİSEL VERİLERİN KİMLERE VE HANGİ AMAÇLA AKTARILABİLECEĞİ</b>
                                <p>
                                    Kanun ve ilgili mevzuat uyarınca uygun güvenlik düzeyini temin etmeye yönelik
                                    gerekli her türlü teknik ve idari tedbirlerin alınmasını sağlayarak, Kişisel
                                    Veri/Kişisel Verilerinizi yukarıda belirtilen amaçlar doğrultusunda;
                                </p>

                                <ul>
                                    <li>
                                        İlgili diğer mevzuat hükümlerinin izin verdiği kişi/kurum ve/veya kuruluşlar
                                    </li>
                                    <li>Özel sigorta şirketleri, bankalar,</li>
                                    <li>Hukuki işlerin takibi amacıyla avukatlar veya avukatlık ortaklıkları</li>
                                    <li>Doğrudan/dolaylı yurtiçi hissedarlarımız</li>
                                    <li>Bağlı ortaklıklarımız ve/veya iştiraklerimiz</li>
                                    <li>Denetçiler</li>
                                    <li>Danışmanlar</li>
                                    <li>İş Ortakları</li>
                                </ul>
                                <p>
                                    ve faaliyetlerimizi yürütmek üzere sözleşmesel olarak hizmet aldığımız yurtiçi
                                    kuruluşlar ile KVK Kanunu’nun 8. maddesinde belirtilen kişisel veri işleme şartları
                                    ve amaçları çerçevesinde aktarılabilecektir
                                </p>
                                <b>6. VERİ GÜVENLİĞİ TEDBİRLERİ</b>
                                <p>
                                    Kişisel veriler; İnohom Kişisel Veri Koruma Politika'sında belirtilen idari ve
                                    teknik tedbirler doğrultusunda korunmakta ve saklanmaktadır.
                                </p>
                                <p>
                                    Fiziki ortamda muhafaza edilen kişisel veriler, üçüncü kişilerin göremeyeceği
                                    şekilde dosyalanmakta ve kilitli dolapta saklanmakta fiziki veya dijital ortamda
                                    muhafaza edilen kayıtlara yalnızca sınırlı sayıda İnohom çalışanının erişimi
                                    bulunmaktadır. Kişisel verilere erişim yetkisi bulunan tüm personel; gizlilik
                                    taahhütnamesi ile erişilen verilerin gizliliğini koruyacağını beyan etmektedir. 
                                </p>
                                <b>7. KİŞİSEL VERİLERİNİZİn saklanma süreleri</b>
                                <p>
                                    Kişisel verileriniz, Aydınlatma Metninde belirtilen amaçlarla sınırlı olmak üzere;
                                    Firmamızın tabi olduğu yasal mevzuat kapsamında yer alan veri işleme ve zamanaşımı
                                    sürelerine riayet edilerek işlenecektir. Kanunlarda veri işleme sürelerine ilişkin
                                    değişiklik yapılması halinde, belirlenen yeni süreler esas alınacaktır.
                                </p>
                                <p>
                                    Kişisel verileriniz, amaçla sınırlılık ilkesinin bir gereği olarak işbu Aydınlatma
                                    Metninde açıklanan amaçların yerine getirilmesi ve her halükârda kanuni süreler,
                                    firma uygulamaları ve ticari yaşamının teamülleri uyarınca işlenmesini gerektiren
                                    süre ile sınırlı olarak işlenmekte, sürelerin dolması ardından ise silinmekte, yok
                                    edilmekte veya anonim hale getirilmektedir. Kişisel verilerinizin saklanma
                                    sürelerine ilişkin detaylı bilgi almak isterseniz ilgili kişinin hakları bölümünde
                                    bulunan adresler üzerinden bizimle iletişime geçebilirsiniz.
                                </p>
                                <b>8. KİŞİSEL VERİ SAHİBİNİN 6698 SAYILI KANUN’UN 11. MADDESİNDE SAYILAN HAKLARI</b>
                                <p>
                                    Kişisel veri sahibi olarak Kanun’un 11. maddesi uyarınca aşağıdaki haklara sahip
                                    olduğunuzu bildiririz:
                                </p>
                                <p>
                                    Kişisel veri işlenip işlenmediğini öğrenme, Kişisel verileri işlenmişse buna ilişkin
                                    bilgi talep etme, Kişisel verilerin işlenme amacını ve bunların amacına uygun
                                    kullanılıp kullanılmadığını öğrenme, Yurt içinde veya yurt dışında kişisel verilerin
                                    aktarıldığı üçüncü kişileri bilme, Kişisel verilerin eksik veya yanlış işlenmiş
                                    olması halinde, bunların düzeltilmesini isteme ve bu kapsamda yapılan işlemin
                                    kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme, 6698 sayılı
                                    Kanun ve ilgili diğer kanun hükümlerine uygun olarak işlenmiş olmasına rağmen,
                                    işlenmesini gerektiren sebeplerin ortadan kalkması hâlinde, kişisel verilerin
                                    silinmesini veya yok edilmesini isteme ve bu kapsamda yapılan işlemin kişisel
                                    verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme, İşlenen verilerin
                                    münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişinin kendisi
                                    aleyhine bir sonucun ortaya çıkmasına itiraz etme, Kişisel verilerin kanuna aykırı
                                    olarak işlenmesi sebebiyle zarara uğraması hâlinde, zararın giderilmesini talep etme
                                    haklarına sahiptir.
                                </p>
                                <p>
                                    Yukarıda belirtilen haklarınıza yönelik başvurularınızı, Veri Sorumlusuna Başvuru
                                    Usul ve Esasları Hakkında Tebliğ hükümlerine uygun şekilde yazılı olarak veya
                                    kayıtlı elektronik posta (KEP) adresi, güvenli elektronik imza, mobil imza veya
                                    İnohom’a daha önce bildirmiş olduğunuz ve sistemlerimizde kayıtlı olan e-posta
                                    adresiniz üzerinden elektronik olarak ofisimiz gerçek hak sahibi olduğunuzun
                                    tespitini gerçekleştirebilmesi amacıyla kimliğinizi doğrulayan
                                    bilgilerle/belgelerle* (T.C. kimlik numarası veya diğer ülke vatandaşları için
                                    pasaport numarası, tebligata esas yerleşim yeri adresi/iş yeri adresi, cep
                                    telefonu/telefon/faks numarası, e-posta adresi vb.) birlikte www.inohom.com
                                    adresinden ulaşabileceğiniz İlgili Kişi Başvuru Formu’nu doldurarak Ofisimize
                                    iletebilirsiniz. Talebinizin niteliğine göre en kısa sürede ve en geç 30 (otuz) gün
                                    içinde başvurularınız ücretsiz olarak sonuçlandırılacaktır; ancak işlemin ayrıca bir
                                    maliyet gerektirmesi halinde Kişisel Verileri Koruma Kurulu tarafından belirlenecek
                                    tarifeye göre tarafınızdan ücret talep edilebilecektir.
                                </p>
                                <p>
                                    * Söz konusu belgeler kapsamında
                                    <em style={{textDecoration: "underline"}}>
                                        herhangi bir özel nitelikli kişisel veriye (örn. din bilgisi veya kan grubu
                                        bilgisi) yer verilmemesi
                                    </em>
                                      gerektiğini hatırlatmak isteriz
                                </p>
                            </div>
                        )
                    )}
                </div>
                {renderIf(isReadOnly)(
                    () => (
                        <div className="agreement-actions">
                            <button {...onClickHandler(accepted)}>{t("ok")}</button>
                        </div>
                    ),
                    () => (
                        <div className="agreement-actions">
                            <button {...onClickHandler(accepted)}>{t("i_agree")}</button>
                            <button {...onClickHandler(rejected)}>{t("i_do_not_accept")}</button>
                        </div>
                    )
                )}
            </div>
        </div>
    )
})

export const ResetPassword = memo(function ResetPassword() {
    const {t} = useLocalization()
    const history = useHistory()
    const [rpc] = useRPC()
    const dispatch = useDispatch()
    const resetPasswordList = useSelector(state => state.control.userResetPassword)
    const userList = useSelector(state => state.control.userList)
    const username = useSelector(state => state.user?.username)
    const currentUser = useMemo(() => userList.find(u => u?.username === username), [userList, username])
    const {failure} = useMessage()
    const userControl: boolean = currentUser?.first_login && (currentUser?.username === "yonetici" || currentUser?.username === "inoadmin")

    const newPasswordRef = useRef<HTMLInputElement>()
    const newAlarmPasswordRef = useRef<HTMLInputElement>()

    const [setActivePassword, renderNumpad] = useKeyboard("numpad", true)
    const [newPassword, setNewPassword] = useState("")
    const [newAlarmPassword, setNewAlarmPassword] = useState("")
    const [isPassed, setIsPassed] = useState(false)
    const [userAgreement, setUserAgreement] = useState(false)
    const [openUserAgreement, setOpenUserAgreement] = useState(false)

    const passwordValidation = newPassword.match("[0-9]{1}[0-9]{1}[0-9]{1}[0-9]{1}[0-9]{1}[0-9]{1}")
    const alarmPasswordValidation = newAlarmPassword.match("[0-9]{1}[0-9]{1}[0-9]{1}[0-9]{1}")

    useEffect(() => {
        if (!resetPasswordList?.first_login) {
            return history.push("/")
        }
    }, [resetPasswordList, history])

    const onFocusNewPassword = useCallback(
        () => setActivePassword([newPasswordRef, newPassword, setNewPassword]),
        [newPassword, setActivePassword]
    )

    const onFocusNewAlarmPassword = useCallback(
        () => setActivePassword([newAlarmPasswordRef, newAlarmPassword, setNewAlarmPassword]),
        [newAlarmPassword, setActivePassword]
    )

    const onBlur = useCallback(() => setActivePassword(null), [setActivePassword])

    const isFieldsValid = useCallback((): boolean => {
        if (userControl) {
            if (newPassword.length !== 6 || newPassword == null || !passwordValidation) {
                return false
            }
            if (newAlarmPassword.length !== 4 || newAlarmPassword == null || !alarmPasswordValidation) {
                return false
            }
        }
        if (currentUser?.username !== "inoadmin" && !userAgreement) {
            return false
        }
        return true
    }, [newPassword, currentUser, newAlarmPassword, passwordValidation, alarmPasswordValidation, userAgreement, userControl])

    const onClickAcceptAgreement = useCallback(() => {
        setUserAgreement(true)
        setOpenUserAgreement(false)
    }, [])

    const onClickRejectAgreement = useCallback(() => {
        setOpenUserAgreement(false)
        setUserAgreement(false)
    }, [])

    const onClickUserAgreements = useCallback(() => {
        setOpenUserAgreement(true)
    }, [])

    const onClick = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }
        if (userControl) {
            return rpc
                .ResetUserPassword(
                    resetPasswordList?.user_name,
                    resetPasswordList?.current_password,
                    newPassword,
                    resetPasswordList?.auth_code,
                    newAlarmPassword
                )
                .then(() => {
                    rpc.doLogout()
                    dispatch(clearUserAction())
                    removeSession()
                    history.push("/login")
                    window.location.reload()
                })
                .catch(failure)
        } else {
            return rpc
                .ResetUserPassword(
                    resetPasswordList?.user_name,
                    resetPasswordList?.current_password,
                    "",
                    resetPasswordList?.auth_code,
                    "",
                    resetPasswordList?.first_login
                )
                .then(() => {
                    history.push("/")
                    window.location.reload()
                })
                .catch(failure)
        }
    }, [dispatch, history, failure, isFieldsValid, rpc, newPassword, newAlarmPassword, resetPasswordList, userControl])

    useEffect(() => {
        if (userControl) {
            if (
                newPassword.length === 6 &&
                newAlarmPassword.length === 4 &&
                passwordValidation &&
                alarmPasswordValidation
            ) {
                if (currentUser?.username === "inoadmin") {
                    setIsPassed(true)
                } else {
                    if(userAgreement) {
                        setIsPassed(true)
                    }
                }
            } else {
                setIsPassed(false)
            }
        } else {
            if (userAgreement) {
                setIsPassed(true)
            } else {
                setIsPassed(false)
            }
        }
    }, [newPassword, newAlarmPassword, passwordValidation, alarmPasswordValidation, currentUser, userAgreement, userControl])

    return (
        <>
            {openUserAgreement && (
                <UserAgreementScreen accepted={onClickAcceptAgreement} rejected={onClickRejectAgreement} />
            )}
            <div className="reset-password-container">
                <div className="reset-password">
                    <span>{Boolean(userControl) ? t("set_new_password") : t("user_agreements")}</span>
                    {renderIf(Boolean(userControl))(() => (
                        <>
                            <input
                                type="password"
                                maxLength={6}
                                placeholder={t("new_password")}
                                ref={newPasswordRef}
                                onChange={e => setNewPassword(e.target.value)}
                                className="reset-input-button"
                                onFocus={onFocusNewPassword}
                                onBlur={onBlur}
                                value={newPassword}
                            />
                            <input
                                type="password"
                                maxLength={4}
                                placeholder={t("new_alarm_password")}
                                ref={newAlarmPasswordRef}
                                onChange={e => setNewAlarmPassword(e.target.value)}
                                className="reset-input-button"
                                onFocus={onFocusNewAlarmPassword}
                                onBlur={onBlur}
                                value={newAlarmPassword}
                            />
                        </>
                    ))}
                    {renderIf(currentUser?.username !== "inoadmin")(() => (
                        <div className="reset-password-user-agreements-container">
                            <RadioButton title={""} isSelected={userAgreement} onClick={onClickUserAgreements} />
                            <span onClick={onClickUserAgreements}>
                                Kullanıcı ve Gizlilik Sözleşmesi
                                <span style={{textDecoration: "underline"}}>(Görüntüle)</span>
                            </span>
                        </div>
                    ))}
                    <button
                        className="reset-button"
                        {...onClickHandler(onClick)}
                        disabled={!isPassed}
                        style={{backgroundColor: isPassed ? "#31d10065" : "rgb(119, 119, 119)"}}
                    >
                        {t("approve")}
                    </button>
                </div>
                {renderNumpad()}
            </div>
        </>
    )
})
